'use client';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/components/ui/carousel';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { useRootStore } from '@/providers/root-store-provider';
import formChooseServiceSchema from '@/schemas/formChooseService';
import type { TOrganizationService } from '@/services/api/services-api';
import { currencyFormat } from '@/utils/currency';
import { Mixpanel } from '@/utils/mixPanel';
import { zodResolver } from '@hookform/resolvers/zod';
import { filter } from 'lodash';
import { CheckCircle, Circle } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { FormChooseServiceV2Props } from './FormChooseService-v2.types';

type TForm = z.input<ReturnType<typeof formChooseServiceSchema>>;

const FormChooseServiceV2: React.FC<FormChooseServiceV2Props> = ({
  services,
  organization
}) => {
  const { t } = useTranslation('newBooking');
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [category, setCategory] = useState<TOrganizationService[]>();

  //store
  const addStep = useRootStore((store) => store.addStep);
  const currentStep = useRootStore((store) => store.currentStep);
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );
  const stepServicesData = useRootStore((store) => store.stepServicesData);

  const SelectedServiceRef = useRef<null | TOrganizationService>(null);
  const minParticipants = SelectedServiceRef.current?.minConsumers ?? 1;
  const maxParticipants = SelectedServiceRef.current?.maxConsumers ?? 4;

  // Initializing useForm hook with the schema and default values
  const form = useForm<TForm>({
    resolver: zodResolver(
      formChooseServiceSchema({ minParticipants, maxParticipants })
    ),
    defaultValues: {
      service: stepServicesData?.service,
      participants: stepServicesData?.participants
    }
  });

  // get price and participant from form with listeners
  const service = useWatch({ control: form.control, name: 'service' });
  const price = useWatch({ control: form.control, name: 'service' })?.price;

  const participants = useWatch({
    control: form.control,
    name: 'participants'
  });

  const selectedService = form.getValues().service;
  const total = price * Number(participants);
  const recervationPct = selectedService?.reservationPct;
  const deposit = total * (recervationPct / 100);

  async function onSubmit(data: TForm) {
    const service = data.service;
    const participants = Number(data.participants);
    loadStepServicesData({
      service,
      participants
    });

    addStep(currentStep);
  }

  const serviceCategory = {
    Windsurf: filter(services, (item) =>
      item.name.toLowerCase().includes('windsurf')
    ),
    'Paddle surf': filter(services, (item) =>
      item.name.toLowerCase().includes('paddle surf')
    )
  };

  type ServiceKeyType = keyof typeof serviceCategory;

  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      const categorySelected = Object.keys(serviceCategory)[
        api.selectedScrollSnap()
      ] as ServiceKeyType;

      setCategory(serviceCategory[categorySelected]);
      setCurrent(api.selectedScrollSnap() + 1);
    });

    const categorySelected = Object.keys(serviceCategory)[
      api.selectedScrollSnap()
    ] as ServiceKeyType;

    setCategory(serviceCategory[categorySelected]);
  }, [api]);

  return (
    <Form {...form}>
      <h2 className="font-bold mb-6">{t('stepServiceTitle')}</h2>
      <form onSubmit={form.handleSubmit(onSubmit)} className={`space-y-8`}>
        <div>
          <Carousel
            setApi={setApi}
            opts={{
              loop: true
            }}
          >
            <CarouselContent>
              {Object.keys(serviceCategory).map((serviceName) => {
                return (
                  <CarouselItem key={serviceName}>
                    <div className="p-1">
                      <Card>
                        <CardHeader className="flex flex-row justify-center items-center">
                          <CardTitle>{serviceName}</CardTitle>
                        </CardHeader>
                      </Card>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <div className="flex gap-2 justify-center pt-2">
              <CarouselPrevious
                type="button"
                className={'relative left-0 translate-y-0'}
              />
              <div className="py-2 text-center text-sm text-muted-foreground">
                {t('count', { current, count })}
              </div>
              <CarouselNext
                type="button"
                className="relative right-0 translate-y-0"
              />
            </div>
          </Carousel>
        </div>

        <FormField
          control={form.control}
          name="service"
          render={({ field }) => {
            return (
              <FormItem>
                <FormItem className="flex flex-wrap gap-5 space-y-0 justify-center">
                  {category?.map((service) => {
                    const isSelected = field?.value?.id === service.id;
                    return (
                      <Card
                        key={`${service.name}`}
                        className={`cursor-pointer 
                        w-[20rem] min-h-[20rem] flex flex-col duration-200
                        ${
                          isSelected
                            ? 'border-primary drop-shadow-[0_10px_10px_hsl(var(--primary)/.2)]'
                            : ''
                        }`}
                        onClick={() => {
                          Mixpanel.track('press select activity', {
                            screen: 'New Booking',
                            Activity: service?.name
                          });
                          field.onChange(service);
                          form.setValue('participants', service.minConsumers);
                        }}
                      >
                        <CardHeader className="flex flex-row justify-between items-center">
                          <CardTitle>{service.name}</CardTitle>
                          <CardTitle className="ml-4">
                            {currencyFormat(
                              service.price,
                              organization.currency
                            )}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="flex flex-1 text-ellipsis overflow-hidden">
                          <p className="">{service.description}</p>
                        </CardContent>
                        <CardFooter className="w-full justify-end items-end">
                          {isSelected ? (
                            <Button type="button" variant={'outline'}>
                              {t('selected')}
                            </Button>
                          ) : (
                            <Button type="button" variant={'default'}>
                              {t('select')}
                            </Button>
                          )}
                        </CardFooter>
                      </Card>
                    );
                  })}
                </FormItem>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="participants"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>{t('numberOfParticipants')}</FormLabel>
                <FormItem className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 space-y-0 gap-3">
                  {[...Array(selectedService?.maxConsumers)].map((_, i) => {
                    const slot = i + 1;
                    return (
                      <Button
                        type="button"
                        key={slot}
                        onClick={() => {
                          field.onChange(slot);
                          Mixpanel.track('select time', {
                            screen: 'New Booking',
                            timeSelected: slot
                          });
                        }}
                        variant={field.value === slot ? 'default' : 'outline'}
                        className="flex justify-start h-16"
                      >
                        <div>
                          {field.value === slot ? (
                            <CheckCircle width={20} />
                          ) : (
                            <Circle className="text-zinc-400" width={20} />
                          )}
                        </div>
                        <p className="text-base ml-3">{slot}</p>
                      </Button>
                    );
                  })}
                </FormItem>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        {total ? (
          <div className="w-full bg-accent rounded p-3 space-y-2">
            <div className="flex justify-between">
              <p>{t('stepParticipants.total')}</p>
              <p>
                {currencyFormat(
                  form.control._formValues?.service?.price *
                    Number(form.control._formValues?.participants),
                  organization.currency
                )}
              </p>
            </div>
            <div className="flex justify-between">
              <p>{t('stepParticipants.deposit')}</p>
              <p>{currencyFormat(deposit, organization.currency)}</p>
            </div>
          </div>
        ) : null}
        <Button
          onClick={() => {
            Mixpanel.track('press continue button', {
              screen: 'New Booking'
            });
          }}
          type="submit"
          className="w-full"
        >
          {t('stepParticipants.form.continue')}
        </Button>
      </form>
    </Form>
  );
};

export default FormChooseServiceV2;
